// Field Notes from the In-Between — entry point.

import './styles/main.css';
import './styles/typography.css';
import './styles/chapters.css';
import './styles/polish.css';

import { inject as injectAnalytics } from '@vercel/analytics';
import { injectSpeedInsights } from '@vercel/speed-insights';

injectAnalytics();
injectSpeedInsights();

import { initChapterState } from './essay/chapterState';
import { initChapterRail } from './essay/chapterRail';
import { initProgress } from './essay/progress';
import { initKineticReveal } from './essay/kineticReveal';
import { initFirstTerminal } from './essay/firstTerminal';
import { initReaderMode } from './essay/readerMode';
import { initSubscribe } from './essay/subscribe';
import { initHeroAtmosphere } from './shaders/heroAtmosphere';
import { initSilmarillionResolve } from './shaders/silmarillionResolve';
import { initBookOrbs } from './shaders/bookOrbs';

// ---- Chapter-as-landscape state machine ----
initChapterState();
initChapterRail();
initProgress();
initKineticReveal();
initFirstTerminal();
initReaderMode();
initSubscribe();

// ---- Terminal cursor: append blinking cursor to the last AI line of each terminal ----
document.querySelectorAll('.dlg:not(.dlg--human)').forEach((dlg) => {
  const otherLines = dlg.querySelectorAll('.v-other');
  const last = otherLines[otherLines.length - 1];
  if (!last) return;
  const cursor = document.createElement('span');
  cursor.className = 'terminal-cursor';
  cursor.setAttribute('aria-hidden', 'true');
  last.appendChild(cursor);
});

// ---- Reveal observer (excludes .pq--door — that has its own timing) ----
const io = new IntersectionObserver(
  (entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('visible');
        io.unobserve(e.target);
      }
    });
  },
  { threshold: 0.12, rootMargin: '0px 0px -30px 0px' }
);
document.querySelectorAll('.rv, .pq').forEach((el) => {
  if (!el.classList.contains('pq--door')) io.observe(el);
});

// ---- Door ceremony: fires only when the pull quote reaches the
// upper reading band of the viewport (~15%–50% down from top). Before
// this, the generic observer was triggering the animation the moment
// the quote first peeked into view from the bottom — the reader would
// still be scrolling toward it. Now the bloom + scribe rule fire at
// the reading position, which is what "that was the door" demands. ---
const door = document.querySelector<HTMLElement>('.pq--door');
if (door) {
  const doorIo = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('visible');
        const section = e.target.closest('.s--door');
        if (section) {
          section.classList.add('ceremony-door');
          setTimeout(() => section.classList.remove('ceremony-door'), 3000);
        }
        doorIo.unobserve(e.target);
      });
    },
    { rootMargin: '-15% 0px -50% 0px', threshold: 0 }
  );
  doorIo.observe(door);
}

// ---- Silmarillion morph: orb color flip + ceremony wash ----
const morphOrb = document.getElementById('silmarillion-orb');
if (morphOrb) {
  const crisisSection = document.querySelector('.s--crisis');
  const mio = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setTimeout(() => {
            morphOrb.classList.add('morphed');
            if (crisisSection) {
              crisisSection.classList.add('ceremony-silmarillion');
              setTimeout(() => crisisSection.classList.remove('ceremony-silmarillion'), 3200);
            }
          }, 500);
          mio.unobserve(e.target);
        }
      });
    },
    { threshold: 0.6 }
  );
  mio.observe(morphOrb);
}

// ---- Dark mode toggle ----
const toggle = document.getElementById('themeToggle')!;
function applyTheme(t: string) {
  document.documentElement.setAttribute('data-theme', t);
  try {
    localStorage.setItem('theme', t);
  } catch (_e) {}
}
toggle.addEventListener('click', () => {
  const root = document.documentElement;
  const current = root.getAttribute('data-theme');
  root.classList.add('theme-flipping');
  applyTheme(current === 'dark' ? 'light' : 'dark');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => root.classList.remove('theme-flipping'));
  });
});
requestAnimationFrame(() => {
  requestAnimationFrame(() => toggle.classList.add('ready'));
});

// ============================================================================
// Shader stack — gated by WebGL2 + reduced-motion. Each shader module
// no-ops if its host element isn't on the page.
// ============================================================================

const hasWebGL2 = 'WebGL2RenderingContext' in window;

if (hasWebGL2) {
  initHeroAtmosphere();
  initSilmarillionResolve();
  initBookOrbs();
}
